import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import zhCN from './locales/zh-CN.json';
import zhTW from './locales/zh-TW.json';
import ms from './locales/ms.json';
import id from './locales/id.json';

/** Same codes as the VIO Member App (its SUPPORTED_LANGS). */
export const SUPPORTED_LANGS = ['en', 'zh-CN', 'zh-TW', 'ms', 'id'];

const DATE_LOCALES = {
  en: 'en-US',
  'zh-CN': 'zh-CN',
  'zh-TW': 'zh-TW',
  ms: 'ms-MY',
  id: 'id-ID',
};

/**
 * Map whatever the host sends onto a supported code. Region tags ('en-US',
 * 'zh-HK') and bare 'zh' are folded the same way the Member App folds them.
 */
export function normalizeLang(raw) {
  if (!raw || typeof raw !== 'string') return null;
  if (SUPPORTED_LANGS.includes(raw)) return raw;
  const [base, region] = raw.replace('_', '-').split('-');
  if (base.toLowerCase() === 'zh') {
    return ['TW', 'HK', 'MO', 'Hant'].includes(region) ? 'zh-TW' : 'zh-CN';
  }
  const lower = base.toLowerCase();
  return SUPPORTED_LANGS.includes(lower) ? lower : null;
}

/**
 * The Member App passes `?lang=` on the iframe URL. Without it (an older host,
 * or "Open in new tab") stay in English rather than guess from the device, so
 * the mini app never disagrees with the language the member picked in VIO.
 */
function initialLang() {
  const params = new URLSearchParams(window.location.search);
  return normalizeLang(params.get('lang')) || 'en';
}

export function applyLocale(raw) {
  const lang = normalizeLang(raw);
  if (lang && lang !== i18n.language) i18n.changeLanguage(lang);
}

export function getDateLocale() {
  return DATE_LOCALES[i18n.language] || 'en-US';
}

i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    'zh-CN': { translation: zhCN },
    'zh-TW': { translation: zhTW },
    ms: { translation: ms },
    id: { translation: id },
  },
  lng: initialLang(),
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_LANGS,
  interpolation: { escapeValue: false },
});

// The host may also push the language without reloading the iframe:
// `VIO_LOCALE { locale }` or `locale` on `VIO_AUTH_TOKENS`.
const parentOrigin = (() => {
  try {
    const raw = new URLSearchParams(window.location.search).get('parentOrigin') || document.referrer;
    return raw ? new URL(raw).origin : null;
  } catch {
    return null;
  }
})();

window.addEventListener('message', (event) => {
  if (!parentOrigin || event.origin !== parentOrigin) return;
  const { type, payload } = event.data || {};
  if (type === 'VIO_LOCALE' || type === 'VIO_AUTH_TOKENS') applyLocale(payload?.locale);
});

export default i18n;
